"use client"

import React, { useState, useEffect, useMemo } from "react";
import dynamic from 'next/dynamic';
import { Language, useLanguages } from '@/contexts/LanguageContext';
import { useIdPath } from '@/contexts/IdPathProvider';
import assetServices from "../../utils/services/asset";
import fileManagerServices from "../../utils/services/fileManager";
import LayoutDefault from "../../layouts/LayoutDefault";
import { useToast } from "@/templates/Admin/components/Toast";
import { useAutoTranslate } from '@/hooks/useAutoTranslate';
import { sanitizeHtml } from '@/utils/sanitize-html';



// Dynamic import CKEditor
const CKEditor = dynamic(() => import('@/components/CKEditor/CKEditorWrapper'), {
    ssr: false,
    loading: () => <div className="animate-pulse bg-gray-200 h-96 rounded"></div>
});

const FALLBACK_LANGUAGES: Language[] = [
    {
        id: 'vn',
        name: 'Tiếng Việt',
        code: 'vn',
        flag: 'vn',
        enabled: true,
        isDefault: true,
    },
    {
        id: 'en',
        name: 'English',
        code: 'en',
        flag: 'us',
        enabled: true,
    },
];

type NormalizedLanguage = {
    code: string;
    label: string;
};

const normalizeRecordValues = (record?: Record<string, string>): Record<string, string> => {
    if (!record) {
        return {};
    }
    return Object.entries(record).reduce<Record<string, string>>((acc, [key, value]) => {
        if (typeof value !== 'string') {
            return acc;
        }
        const normalizedKey = key.toLowerCase();
        if (normalizedKey) {
            acc[normalizedKey] = value;
        }
        return acc;
    }, {});
};

interface BlogPost {
    uuid?: string;
    title: Record<string, string>;
    subtitle?: Record<string, string>;
    slug: string;
    content: Record<string, string>;
    excerpt: Record<string, string>;
    featuredImage?: string;
    type?: string;
    categories: string[];
    tags: string[];
    seo?: {
        title: Record<string, string>;
        description: Record<string, string>;
        keywords: string[];
    };
    scheduledAt?: Date;
    createdAt?: string | Date;
    updatedAt?: string | Date;
    creator?: { id: number; name: string };
    updater?: { id: number; name: string };
    viewCount?: number;
}

interface ArticleManagementProps {
    onClose: () => void;
    isSidebarOpen: boolean;
    mode?: 'create' | 'update';
    // initialData?: BlogPost;
    initialData?: any;
    parentCategory?: any;
}

interface BlogFormData {
    title: Record<string, string>;
    subtitle: Record<string, string>;
    content: Record<string, string>;
    featuredImage?: string;
    type?: string;
    // categories and tags removed from editor
    scheduledAt?: Date;
}

export default function ArticleManagement({ onClose, isSidebarOpen, mode = 'create', initialData, parentCategory }: ArticleManagementProps) {
    console.log('ArticleManagement initialData:', initialData);
    const { languages, currentLanguage } = useLanguages();
    const { showSuccess, showError } = useToast();
    const languageOptions = useMemo(
        () => (languages.length ? languages : FALLBACK_LANGUAGES),
        [languages]
    );

    // Auto translate hook
    const { isTranslating, translateAllFields, translateSingleField } = useAutoTranslate({
        languages: languageOptions,
        showSuccess: (message: string) => {
            showSuccess(message || 'Thành công', 'Success');
        },
        showError: (message: string) => {
            showError(message || 'Lỗi', 'Error');
        },
        showWarning: (message: string) => {
            // Use toast for warning
            showError(message || 'Cảnh báo', 'Warning');
        }
    });
    
    const normalizedDefaultLanguage = useMemo(() => {
        const fallbackCode = languageOptions[0]?.code || 'vn';
        const currentCode = currentLanguage?.code || fallbackCode;
        return currentCode.toLowerCase();
    }, [currentLanguage?.code, languageOptions]);
    const { idPath, namePath } = useIdPath();
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    // categories and tags removed for editor UI
    const [activeLanguage, setActiveLanguage] = useState(normalizedDefaultLanguage);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageUrlInput, setImageUrlInput] = useState('');
    const [blogFormVariables, setBlogFormVariables] = useState<any[]>([]);
    const hasThumbnailVariable = useMemo(
        () => blogFormVariables.some((variable) => variable.asset_variable_name === 'thumbnail'),
        [blogFormVariables]
    );
    
    // Translation state similar to GroupDetailModal
    const [editedTranslations, setEditedTranslations] = useState<{[key: string]: {title: string, subtitle: string, content: string}}>({});
    const [localTranslations, setLocalTranslations] = useState<any[]>([]);

    const [formData, setFormData] = useState<BlogFormData>({
        title: {},
        subtitle: {},
        content: {},
        featuredImage: undefined,
        type: undefined,
        scheduledAt: undefined,
    });

    console.log('formData:', formData);

    useEffect(() => {
        setActiveLanguage(normalizedDefaultLanguage);
    }, [normalizedDefaultLanguage]);

    const FEATURED_IMAGE_SUBPATH = 'blogs/featured-images';
    const getFeaturedFolderUrl = () => {
        const baseUrl = fileManagerServices.createFolderUrl(namePath || '');
        const trimmedBase = baseUrl.replace(/\/+$/g, '');
        const trimmedSubpath = FEATURED_IMAGE_SUBPATH.replace(/^\/+|\/+$/g, '');
        const folderUrl = trimmedBase ? `${trimmedBase}/${trimmedSubpath}` : `/upload/${trimmedSubpath}`;
        return folderUrl;
    };

    const resolveUploadUrl = (uploadResponse: any, fallbackUrl: string) => {
        const sources = [uploadResponse?.data, uploadResponse?.data?.data, uploadResponse];
        const keys = ['file_url', 'fullUrl', 'url', 'fileUrl', 'path'];

        for (const source of sources) {
            if (!source || typeof source !== 'object') continue;

            for (const key of keys) {
                const candidate = source[key];
                if (typeof candidate === 'string' && candidate.trim()) {
                    return candidate;
                }
            }

            if (Array.isArray(source.files) && source.files.length > 0) {
                const fileEntry = source.files[0];
                for (const key of keys) {
                    const candidate = fileEntry?.[key];
                    if (typeof candidate === 'string' && candidate.trim()) {
                        return candidate;
                    }
                }
            }
        }

        return fallbackUrl;
    };

    const BLOG_ASSET_TYPE_ID = 7;

    const arrayVariablesBlog = useMemo(() => {
        const normalizedLanguages: NormalizedLanguage[] = languageOptions
            .map((lang) => {
                const codeCandidate = lang.id || lang.code || '';
                return {
                    code: codeCandidate.toLowerCase(),
                    label: lang.name || lang.code || lang.id || '',
                };
            })
            .filter((lang): lang is NormalizedLanguage => Boolean(lang.code));

        const languageVariables = normalizedLanguages.flatMap((lang, index) => {
            const labelSuffix = lang.label ? ` ${lang.label}` : '';
            const baseIndex = index * 3;
            return [
                {
                    asset_variable_name: `title_${lang.code}`,
                    asset_variable_type: 'text',
                    description: `Tiêu đề bài viết${labelSuffix}`,
                    index: baseIndex + 1,
                },
                {
                    asset_variable_name: `subtitle_${lang.code}`,
                    asset_variable_type: 'text',
                    description: `Tiêu đề phụ${labelSuffix}`,
                    index: baseIndex + 2,
                },
                {
                    asset_variable_name: `description_${lang.code}`,
                    asset_variable_type: 'text',
                    description: `Mô tả ngắn${labelSuffix}`,
                    index: baseIndex + 3,
                },
            ];
        });

        // Thêm biến type
        languageVariables.push({
            asset_variable_name: 'type',
            asset_variable_type: 'text',
            description: 'Loại bài viết',
            index: languageVariables.length + 1,
        });

        return languageVariables;
    }, [languageOptions]);

    useEffect(() => {
        if (!idPath) {
            return;
        }

        const loadBlogVariables = async () => {
            try {
                const response = await assetServices.getFormAssetByType({
                    id_path: idPath,
                    type: BLOG_ASSET_TYPE_ID.toString(),
                }) as any;

                const variables = response?.data?.variables ?? response?.variables ?? [];
                if (response.success) {
                    setBlogFormVariables(variables);
                }
            } catch (error) {
                console.error('Error loading blog variables:', error);
            }
        };

        loadBlogVariables();
    }, [idPath]);

    // Categories/tags loading removed for editor UI; no initial load required

    // Initialize localTranslations from languages
    useEffect(() => {
        if (languageOptions.length > 0) {
            const translations = languageOptions.map(lang => ({
                language_id: lang.id,
                language_name: lang.name,
                language_full_name: lang.name,
                title: '',
                subtitle: '',
                content: ''
            }));
            setLocalTranslations(translations);
        }
    }, [languageOptions]);

    // Load initial data from API or props
    useEffect(() => {
        if (mode === 'update' && initialData) {
            setFormData({
                title: normalizeRecordValues(initialData.title),
                subtitle: normalizeRecordValues(initialData.subtitle),
                content: normalizeRecordValues(initialData.content),
                featuredImage: initialData.featuredImage,
                type: initialData.type,
                scheduledAt: initialData.scheduledAt,
            });
            
            // Initialize editedTranslations with existing data
            const initialEdited: {[key: string]: {title: string, subtitle: string, content: string}} = {};

            languageOptions.forEach(lang => {
                const langCode = lang.id || '';
                initialEdited[lang.id] = {
                    title: initialData.title?.[langCode] || '',
                    subtitle: initialData.subtitle?.[langCode] || '',
                    content: initialData.content?.[langCode] || ''
                };
            });
            setEditedTranslations(initialEdited);
            
            if (initialData.featuredImage) {
                setImagePreview(initialData.featuredImage);
                setImageUrlInput(initialData.featuredImage);
            }
        }
    }, [mode, initialData, languageOptions]);

    // loadInitialData removed - categories/tags management moved to asset manager

    // Handle title change for specific language
    const handleTitleChange = (languageId: string, title: string) => {
        setEditedTranslations(prev => ({
            ...prev,
            [languageId]: {
                ...prev[languageId],
                title
            }
        }));
    };

    // Handle subtitle change for specific language
    const handleSubtitleChange = (languageId: string, subtitle: string) => {
        setEditedTranslations(prev => ({
            ...prev,
            [languageId]: {
                ...prev[languageId],
                subtitle
            }
        }));
    };

    // Handle content change for specific language
    const handleContentChange = (languageId: string, content: string) => {
        setEditedTranslations(prev => ({
            ...prev,
            [languageId]: {
                ...prev[languageId],
                content
            }
        }));
    };

    // Adapter setters for useAutoTranslate to merge results into `editedTranslations` state
    const setTitlesFromTranslate = (dataOrUpdater: any) => {
        if (typeof dataOrUpdater === 'function') {
            // build current titles map
            const currentTitles: {[k: string]: string} = {};
            (localTranslations || []).forEach((t: any) => {
                const edited = editedTranslations[t.language_id];
                currentTitles[t.language_id] = edited?.title ?? t.title ?? '';
            });
            const newTitles = dataOrUpdater(currentTitles);
            setEditedTranslations(prev => {
                const updated = { ...prev };
                Object.entries(newTitles).forEach(([langId, title]) => {
                    updated[langId] = {
                        ...updated[langId],
                        title: title as string
                    };
                });
                return updated;
            });
        } else {
            const newTitles = dataOrUpdater || {};
            setEditedTranslations(prev => {
                const updated = { ...prev };
                Object.entries(newTitles).forEach(([langId, title]) => {
                    updated[langId] = {
                        ...updated[langId],
                        title: title as string
                    };
                });
                return updated;
            });
        }
    };

    const setSubtitlesFromTranslate = (dataOrUpdater: any) => {
        if (typeof dataOrUpdater === 'function') {
            const currentSubtitles: {[k: string]: string} = {};
            (localTranslations || []).forEach((t: any) => {
                const edited = editedTranslations[t.language_id];
                currentSubtitles[t.language_id] = edited?.subtitle ?? t.subtitle ?? '';
            });
            const newSubtitles = dataOrUpdater(currentSubtitles);
            setEditedTranslations(prev => {
                const updated = { ...prev };
                Object.entries(newSubtitles).forEach(([langId, subtitle]) => {
                    updated[langId] = {
                        ...updated[langId],
                        subtitle: subtitle as string
                    };
                });
                return updated;
            });
        } else {
            const newSubtitles = dataOrUpdater || {};
            setEditedTranslations(prev => {
                const updated = { ...prev };
                Object.entries(newSubtitles).forEach(([langId, subtitle]) => {
                    updated[langId] = {
                        ...updated[langId],
                        subtitle: subtitle as string
                    };
                });
                return updated;
            });
        }
    };

    const setContentsFromTranslate = (dataOrUpdater: any) => {
        if (typeof dataOrUpdater === 'function') {
            const currentContents: {[k: string]: string} = {};
            (localTranslations || []).forEach((t: any) => {
                const edited = editedTranslations[t.language_id];
                currentContents[t.language_id] = edited?.content ?? t.content ?? '';
            });
            const newContents = dataOrUpdater(currentContents);
            setEditedTranslations(prev => {
                const updated = { ...prev };
                Object.entries(newContents).forEach(([langId, content]) => {
                    updated[langId] = {
                        ...updated[langId],
                        content: content as string
                    };
                });
                return updated;
            });
        } else {
            const newContents = dataOrUpdater || {};
            setEditedTranslations(prev => {
                const updated = { ...prev };
                Object.entries(newContents).forEach(([langId, content]) => {
                    updated[langId] = {
                        ...updated[langId],
                        content: content as string
                    };
                });
                return updated;
            });
        }
    };

    // Translate all titles, subtitles and contents
    const handleTranslateAllTranslations = async (onlyEmptyFields = false) => {
        try {
            // Prepare maps
            const titleMap: {[k: string]: string} = {};
            const subtitleMap: {[k: string]: string} = {};
            const contentMap: {[k: string]: string} = {};
            (localTranslations || []).forEach((t: any) => {
                const edited = editedTranslations[t.language_id];
                titleMap[t.language_id] = edited?.title ?? t.title ?? '';
                subtitleMap[t.language_id] = edited?.subtitle ?? t.subtitle ?? '';
                contentMap[t.language_id] = edited?.content ?? t.content ?? '';
            });

            // Translate titles (no HTML)
            await translateAllFields(titleMap, setTitlesFromTranslate, 'tiêu đề', false, onlyEmptyFields);

            // Translate subtitles (no HTML)
            await translateAllFields(subtitleMap, setSubtitlesFromTranslate, 'tiêu đề phụ', false, onlyEmptyFields);

            // Translate contents (preserve HTML/styling when possible)
            await translateAllFields(contentMap, setContentsFromTranslate, 'nội dung', false, onlyEmptyFields);

        } catch (error) {
            console.error('Error translating all translations:', error);
        }
    };

    // Translate a single language (title, subtitle and content)
    const handleTranslateSingleLanguage = async (languageId: string) => {
        try {
            const titleMap: {[k: string]: string} = {};
            const subtitleMap: {[k: string]: string} = {};
            const contentMap: {[k: string]: string} = {};
            (localTranslations || []).forEach((t: any) => {
                const edited = editedTranslations[t.language_id];
                titleMap[t.language_id] = edited?.title ?? t.title ?? '';
                subtitleMap[t.language_id] = edited?.subtitle ?? t.subtitle ?? '';
                contentMap[t.language_id] = edited?.content ?? t.content ?? '';
            });

            // Translate title for target language
            await translateSingleField(titleMap, setTitlesFromTranslate, languageId, 'title', 'tiêu đề', false);

            // Translate subtitle for target language
            await translateSingleField(subtitleMap, setSubtitlesFromTranslate, languageId, 'subtitle', 'tiêu đề phụ', false);

            // Translate content for target language
            await translateSingleField(contentMap, setContentsFromTranslate, languageId, 'content', 'nội dung', false);

            showSuccess(`Dịch ${languageId} thành công!`, 'Thành công');
        } catch (error) {
            console.error('Error translating single language:', error);
        }
    };

    const getAttributeValue = (variableName: string) => {
        const [type, ...segments] = variableName.split('_');
        const languageCode = segments.join('_').toLowerCase();
        if (!languageCode) {
            return '';
        }

        if (type === 'title') {
            return formData.title[languageCode] || '';
        }

        if (type === 'subtitle') {
            return formData.subtitle[languageCode] || '';
        }

        if (type === 'description') {
            return formData.content[languageCode] || '';
        }

        return '';
    };

    const buildArticleAttributes = () => {
        const attributes: Record<string, { values: string }> = {};

        // Build attributes from editedTranslations
        Object.entries(editedTranslations).forEach(([languageId, translation]) => {
            const lang = languageOptions.find(l => l.id === languageId);
            if (lang) {
                const langCode = (lang.code || lang.id || '').toLowerCase();
                
                // Only add if value exists and has content after trim
                const titleValue = translation?.title;
                if (titleValue && typeof titleValue === 'string' && titleValue.trim()) {
                    attributes[`title_${langCode}`] = { values: titleValue.trim() };
                }
                
                const subtitleValue = translation?.subtitle;
                if (subtitleValue && typeof subtitleValue === 'string' && subtitleValue.trim()) {
                    attributes[`subtitle_${langCode}`] = { values: subtitleValue.trim() };
                }
                
                const contentValue = translation?.content;
                if (contentValue && typeof contentValue === 'string' && contentValue.trim()) {
                    attributes[`description_${langCode}`] = { values: contentValue.trim() };
                }
            }
        });

        if (hasThumbnailVariable && formData.featuredImage) {
            attributes.thumbnail = { values: formData.featuredImage };
        }

        if (formData.type && formData.type.trim()) {
            attributes.type = { values: formData.type.trim() };
        }

        return attributes;
    };

    // Handle featured image upload
    const handleFeaturedImageUpload = async (file: File) => {
        let tempUrl: string | null = null;
        try {
            setIsUploading(true);
            tempUrl = URL.createObjectURL(file);
            setFormData(prev => ({ ...prev, featuredImage: tempUrl }));
            setImagePreview(tempUrl);
            setImageUrlInput(tempUrl);

            const folderUrl = getFeaturedFolderUrl();
            const fallbackUrl = `${folderUrl.replace(/\/+$/, '')}/${encodeURIComponent(file.name)}`;
            const uploadResult = await fileManagerServices.uploadFiles({ folderUrl, files: [file] }, idPath);

            const resolvedUrl = resolveUploadUrl(uploadResult, fallbackUrl);
            setFormData(prev => ({ ...prev, featuredImage: resolvedUrl }));
            setImagePreview(resolvedUrl);
            setImageUrlInput(resolvedUrl);
            showSuccess('Upload ảnh thành công!', 'Thành công');
        } catch (error) {
            console.error('Error uploading image:', error);
            showError('Upload ảnh thất bại!', 'Lỗi');
        } finally {
            if (tempUrl) {
                URL.revokeObjectURL(tempUrl);
            }
            setIsUploading(false);
        }
    };

    const handleFeaturedImageUrlChange = (value: string) => {
        setImageUrlInput(value);
        setImagePreview(value || null);
        setFormData(prev => ({ ...prev, featuredImage: value || undefined }));
    };

    // Handle content image upload (for CKEditor)
    const handleContentImageUpload = async (file: File): Promise<string> => {
        try {
            // TODO: Implement file upload using asset API
            // For now, create a temporary URL
            const tempUrl = URL.createObjectURL(file);
            return tempUrl;
        } catch (error) {
            console.error('Error uploading content image:', error);
            throw error;
        }
    };

    // Tag handlers removed

    // Handle save
    const handleSave = async () => {
        try {
            setIsSaving(true);
            
            const attributes = buildArticleAttributes();
            
            // Validate: title_vn is required
            if (!attributes.title_vn || !attributes.title_vn.values) {
                showError('Vui lòng nhập tiêu đề tiếng Việt!', 'Lỗi');
                setIsSaving(false);
                return;
            }
            
            const dataToSave: any = {
                ...formData,
                // status: 'published',
                asset_type_id: BLOG_ASSET_TYPE_ID,
                category_uuid: parentCategory || '',
                attributes,
            };

            if (mode === 'create') {
                await assetServices.createNewAsset(idPath, { body: dataToSave });
                showSuccess('Tạo bài viết thành công!', 'Thành công');
            } else if (initialData?.uuid) {
                await assetServices.updateAsset({ 
                    id_path: idPath, 
                    asset_uuid: initialData.uuid, 
                    data: dataToSave 
                });
                showSuccess('Cập nhật bài viết thành công!', 'Thành công');
            }
            
            onClose();
        } catch (error) {
            console.error('Error saving post:', error);
            showError('Lưu bài viết thất bại!', 'Lỗi');
        } finally {
            setIsSaving(false);
        }
    };
    

    return (
        <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen}>
            <div className="flex h-full bg-gray-50">
                {/* Main Content Area - WordPress Style */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-4 bg-white border-b border-gray-200">
                        <div className="flex justify-between items-start">
                            <div className="flex flex-col space-y-4">
                                <div className="flex items-center space-x-4">
                                    <button 
                                        onClick={onClose} 
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                        </svg>
                                    </button>
                                    <h1 className="text-2xl font-semibold text-gray-800">
                                        {mode === 'create' ? 'Tạo bài viết' : 'Chỉnh sửa bài viết'}
                                    </h1>
                                </div>
                                {mode === 'update' && initialData && (
                                    <div className="flex items-center gap-6 text-sm text-gray-600">
                                        {(initialData.updatedAt || initialData.createdAt) && (
                                            <div className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                <div>
                                                    <div className="font-medium">
                                                        Cập nhật: {new Date(initialData.updatedAt || initialData.createdAt).toLocaleString('vi-VN')}
                                                    </div>
                                                    {(initialData.updater || initialData.creator) && (
                                                        <div className="text-xs text-gray-500">
                                                            Người sửa: {(() => {
                                                                const user = initialData.updater || initialData.creator;
                                                                if (typeof user === 'string') return user;
                                                                if (typeof user === 'object' && user?.name) return user.name;
                                                                if (typeof user === 'number') return `ID: ${user}`;
                                                                return 'Unknown';
                                                             })()}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving || isUploading}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >

                                    {isSaving ? 'Đang lưu...' : (mode === 'create' ? 'Tạo bài viết' : 'Lưu thay đổi')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content Editor */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        <div className="max-w-6xl mx-auto space-y-6">
                            {/* Type Selection */}
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">📌 Loại bài viết</h3>
                                <select
                                    value={formData.type || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">-- Chọn loại bài viết --</option>
                                    <option value="featured">Nổi bật</option>
                                    <option value="event">Sự kiện</option>
                                    <option value="travel">Du lịch</option>
                                </select>
                            </div>

                            {/* Featured Image Section */}
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">🖼️ Ảnh đại diện</h3>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file && !isUploading && !isSaving) handleFeaturedImageUpload(file);
                                                }}
                                                disabled={isUploading || isSaving}
                                                className="hidden"
                                                id="featured-image-upload"
                                            />
                                            <label
                                                htmlFor="featured-image-upload"
                                                className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 cursor-pointer ${isUploading || isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                {isUploading ? 'Đang upload...' : 'Upload ảnh'}
                                            </label>
                                            <input
                                                type="text"
                                                value={imageUrlInput}
                                                onChange={(e) => handleFeaturedImageUrlChange(e.target.value)}
                                                placeholder="Dán đường link ảnh"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="relative w-full flex justify-center items-center h-48 rounded-lg border border-dashed border-gray-300 bg-gray-50 overflow-hidden">
                                        {imagePreview ? (
                                            <img
                                                src={imagePreview}
                                                alt="Featured"
                                                className="object-cover w-auto h-full"
                                            />
                                        ) : (
                                            <div className="flex items-center justify-center h-full text-sm text-gray-500">
                                                Chưa có ảnh đại diện. Kích thước khuyến nghị dưới: 1MB
                                            </div>
                                        )}
                                    </div>
                                    {imagePreview && (
                                        <button
                                            onClick={() => {
                                                setImagePreview(null);
                                                setFormData(prev => ({ ...prev, featuredImage: undefined }));
                                                setImageUrlInput('');
                                            }}
                                            className="w-full px-3 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50"
                                        >
                                            Xóa ảnh
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Auto Translate Section */}
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold text-gray-900">🌐 Dịch tự động</h3>
                                    <button
                                        onClick={() => handleTranslateAllTranslations(false)}
                                        disabled={isTranslating}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {isTranslating ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Đang dịch...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                </svg>
                                                Dịch tất cả
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    Dịch tự động các trường trống từ ngôn ngữ mặc định sang các ngôn ngữ khác.
                                </p>
                            </div>

                            {/* Translation List */}
                            {localTranslations && localTranslations.length > 0 ? (
                                <div className="space-y-6">
                                    {localTranslations.map((translation: any) => {
                                        const isEditing = true;
                                        const editedData = editedTranslations[translation.language_id];

                                        return (
                                            <div key={translation.language_id} className={`border rounded-lg p-6 transition-all ${isEditing ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
                                                <div className="flex items-center justify-between mb-6">
                                                    <div className="flex items-center gap-3">
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                                                            {translation.language_name}
                                                        </span>
                                                        {/* <span className="text-sm text-gray-600">{translation.language_full_name}</span> */}
                                                    </div>
                                                    <button
                                                        onClick={() => handleTranslateSingleLanguage(translation.language_id)}
                                                        disabled={isTranslating}
                                                        className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 disabled:opacity-50 flex items-center gap-1"
                                                    >
                                                        {isTranslating ? (
                                                            <>
                                                                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                Đang dịch...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                                </svg>
                                                                Dịch
                                                            </>
                                                        )}
                                                    </button>
                                                </div>

                                                <div className="space-y-6">
                                                    {/* Title Section */}
                                                    <div>
                                                        <label className="text-sm font-semibold mb-3 block text-gray-700">
                                                            📝 Tiêu đề bài viết
                                                        </label>
                                                        {isEditing ? (
                                                            <input
                                                                value={editedData?.title ?? ''}
                                                                onChange={(e) => handleTitleChange(translation.language_id, e.target.value)}
                                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                                                                placeholder="Nhập tiêu đề bài viết..."
                                                            />
                                                        ) : (
                                                            <div className="text-lg font-medium p-4 bg-gray-50 rounded-lg border">
                                                                {translation.title || <span className="text-gray-400 italic">Chưa có tiêu đề</span>}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Subtitle Section */}
                                                    <div>
                                                        <label className="text-sm font-semibold mb-3 block text-gray-700">
                                                            📋 Tiêu đề phụ
                                                        </label>
                                                        {isEditing ? (
                                                            <input
                                                                value={editedData?.subtitle ?? ''}
                                                                onChange={(e) => handleSubtitleChange(translation.language_id, e.target.value)}
                                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                                                                placeholder="Nhập tiêu đề phụ..."
                                                            />
                                                        ) : (
                                                            <div className="text-base p-4 bg-gray-50 rounded-lg border">
                                                                {translation.subtitle || <span className="text-gray-400 italic">Chưa có tiêu đề phụ</span>}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Content Section */}
                                                    <div>
                                                        <label className="text-sm font-semibold mb-3 block text-gray-700">
                                                            📄 Nội dung bài viết
                                                        </label>
                                                        {isEditing ? (
                                                            <div className="space-y-3">
                                                                <CKEditor
                                                                    data={editedData?.content ?? ''}
                                                                    onChange={(data) => handleContentChange(translation.language_id, data)}
                                                                    onImageUpload={handleContentImageUpload}
                                                                    placeholder="Bắt đầu viết nội dung bài viết..."
                                                                    height="400px"
                                                                />
                                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                                                    <div className="text-xs text-blue-700 flex items-center gap-2">
                                                                        <span>💡</span>
                                                                        <span className="font-medium">Hướng dẫn sử dụng Editor:</span>
                                                                    </div>
                                                                    <ul className="text-xs text-blue-600 mt-1 ml-4 space-y-1">
                                                                        <li>• Sử dụng toolbar để định dạng văn bản (in đậm, nghiêng, danh sách...)</li>
                                                                        <li>• Có thể chèn bảng, hình ảnh, liên kết và trích dẫn</li>
                                                                        <li>• Hỗ trợ các tiêu đề và căn chỉnh văn bản</li>
                                                                        <li>• Nội dung sẽ được lưu dưới dạng HTML</li>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="min-h-[300px] p-4 bg-gray-50 rounded-lg text-base leading-relaxed overflow-y-auto border">
                                                                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(translation.content) || '<span class="text-gray-400 italic">Chưa có nội dung</span>' }} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="text-gray-500">
                                        <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <p className="text-lg font-medium">Đang tải ngôn ngữ...</p>
                                        <p className="text-sm">Vui lòng đợi trong giây lát</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

              
            </div>
        </LayoutDefault>
    );
}
